// src/features/tidsregistering/pages/AttendancePage.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Alert, CircularProgress } from '@mui/material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { AttendanceChart } from '../components/attendance';
import CreateTimeEntryDialog from '../components/attendance/CreateTimeEntryDialog';
import CreatePlannedShiftDialog from '../components/attendance/CreatePlannedShiftDialog';
import EditTimeEntryDialog from '../components/attendance/EditTimeEntryDialog';
import { buildAttendanceCategories, groupCategoriesByOverenskomst } from '../adapters/attendanceAdapter';
import { useDayOverviews } from '../hooks/useDayOverviews';
import { usePlannedShifts } from '../hooks/usePlannedShifts';
import { useRegistrationCodes } from '../hooks/useRegistrationCodes';
import { useRegistrations } from '../hooks/useRegistrations';
import { useStaff } from '../../staff/hooks/useStaff';
import { useOverenskomster } from '../hooks/useOverenskomster';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { balancesAPI } from '../services/balances';
import { unavailabilityAPI } from '../services/unavailability';
import { usePermissions } from '../../auth/hooks/usePermissions';

import PageHeader from '../../../components/common/PageHeader';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

function splitAtShiftBoundaries(startTime, endTime, staffShifts) {
  const toMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
  const toHHMM = (min) =>
    `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;

  const startMin = toMin(startTime);
  const endMin = toMin(endTime);
  if (startMin >= endMin) return [{ start_time: startTime, end_time: endTime }];

  const cuts = new Set();
  for (const shift of staffShifts) {
    if (!shift.is_working) continue;
    const s = toMin(shift.start_time);
    const e = toMin(shift.end_time);
    if (s > startMin && s < endMin) cuts.add(s);
    if (e > startMin && e < endMin) cuts.add(e);
  }

  if (cuts.size === 0) return [{ start_time: startTime, end_time: endTime }];

  const sorted = [startMin, ...Array.from(cuts).sort((a, b) => a - b), endMin];
  return sorted.slice(0, -1).map((s, i) => ({
    start_time: toHHMM(s),
    end_time: toHHMM(sorted[i + 1]),
  }));
}

export default function AttendancePage() {
  const { t } = useTranslation(['tidsregistering']);
  const { showError, showSuccess } = useSnackbar();

  const [selectedDate, setSelectedDate] = useState(new Date());
  const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');

  const { dayOverviews, fetchForDepartment, loading: overviewsLoading, error: overviewsError } = useDayOverviews();
  const { plannedShifts, fetchForDepartment: fetchShifts, loading: shiftsLoading, createShift } = usePlannedShifts();
  const { registrationCodes, fetchForDepartment: fetchCodes, loading: codesLoading } = useRegistrationCodes();
  const { staff, fetchDepartmentStaff, loading: staffLoading } = useStaff();
  const { createManual, updateRegistration, deleteRegistration, registrations, fetchForDepartmentDate } = useRegistrations();
  const { overenskomster, fetchForDepartment: fetchOverenskomster, fetchActiveAssignmentsForDepartment } = useOverenskomster();

  // The page shows day overviews; shifts, registrations, codes, agreements and
  // balances each come from their own module and only load with its scope
  const { hasScope } = usePermissions();
  const canReadShifts = hasScope('tidsregistering:planned_shift:read');
  const canCreateShift = canReadShifts && hasScope('tidsregistering:planned_shift:create');
  const canReadRegistrations = hasScope('tidsregistering:registration:read');
  const canReadCodes = hasScope('tidsregistering:registration_code:read');
  const canReadOverenskomster = hasScope('tidsregistering:overenskomst:read');
  const canReadBalances = hasScope('tidsregistering:absence:read');
  // Registrations are entered and edited with a registration code
  const canCreateRegistration = canReadCodes && hasScope('tidsregistering:registration:create');
  const canEditRegistration = canReadRegistrations && canReadCodes && hasScope('tidsregistering:registration:update');
  const canDeleteRegistration = hasScope('tidsregistering:registration:delete');

  const [activeAssignments, setActiveAssignments] = useState([]);
  const [unavailabilityBlocks, setUnavailabilityBlocks] = useState([]);

  // Lazy balance cache: staffMemberId -> balance[]
  const [balancesMap, setBalancesMap] = useState({});
  const fetchBalancesForStaff = useCallback(async (staffMemberId) => {
    if (balancesMap[staffMemberId] !== undefined) return;
    // Optimistically mark as loading so concurrent hovers don't double-fetch
    setBalancesMap((prev) => ({ ...prev, [staffMemberId]: null }));
    try {
      const result = await balancesAPI.listForStaff(staffMemberId);
      setBalancesMap((prev) => ({ ...prev, [staffMemberId]: result.data?.balances ?? [] }));
    } catch {
      setBalancesMap((prev) => ({ ...prev, [staffMemberId]: [] }));
    }
  }, [balancesMap]);

  // Dialog state for drag-to-create registration (bottom track)
  const [regDialogOpen, setRegDialogOpen] = useState(false);
  const [regDialogInitial, setRegDialogInitial] = useState(null);
  const [regCreateLoading, setRegCreateLoading] = useState(false);
  const [regSubmitError, setRegSubmitError] = useState(null);

  // Dialog state for drag-to-create planned shift (top track)
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [shiftDialogInitial, setShiftDialogInitial] = useState(null);
  const [shiftCreateLoading, setShiftCreateLoading] = useState(false);
  const [shiftSubmitError, setShiftSubmitError] = useState(null);

  // Dialog state for click-to-edit existing registration (bottom track)
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editDialogSlot, setEditDialogSlot] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editDeleteLoading, setEditDeleteLoading] = useState(false);
  const [editSubmitError, setEditSubmitError] = useState(null);

  const loadDayData = useCallback(async (dateStr) => {
    const [overviewResult, shiftResult] = await Promise.all([
      fetchForDepartment(dateStr, dateStr),
      canReadShifts ? fetchShifts(dateStr, dateStr) : null,
      canReadRegistrations ? fetchForDepartmentDate(dateStr) : null,
    ]);
    if (!overviewResult.success) {
      showError(t('tidsregistering:attendance.failed_to_load'));
    }
    return { overviewResult, shiftResult };
  }, [fetchForDepartment, fetchShifts, fetchForDepartmentDate, showError, t, canReadShifts, canReadRegistrations]);

  useEffect(() => {
    const init = async () => {
      const [, , assignmentsResult] = await Promise.all([
        canReadCodes ? fetchCodes() : null,
        fetchDepartmentStaff(),
        canReadOverenskomster ? fetchActiveAssignmentsForDepartment() : null,
        canReadOverenskomster ? fetchOverenskomster() : null,  // populates overenskomster state
      ]);
      if (assignmentsResult?.success) {
        setActiveAssignments(assignmentsResult.data?.assignments ?? []);
      }
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadDayData(selectedDateStr);
  }, [selectedDateStr]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    unavailabilityAPI
      .getDepartmentUnavailability(selectedDateStr, selectedDateStr)
      .then((res) => { if (!cancelled) setUnavailabilityBlocks(res.data?.blocks ?? []); })
      .catch(() => { if (!cancelled) setUnavailabilityBlocks([]); });
    return () => { cancelled = true; };
  }, [selectedDateStr]);

  // ── Registration drag (bottom track) ──────────────────────────────────────
  const handleRegistrationDragCreate = useCallback(({ category, startTime, endTime }) => {
    setRegDialogInitial({ staffMember: category.staffMember, startTime, endTime });
    setRegSubmitError(null);
    setRegDialogOpen(true);
  }, []);

  const handleRegDialogClose = useCallback(() => {
    setRegDialogOpen(false);
    setRegDialogInitial(null);
    setRegSubmitError(null);
  }, []);

  const handleRegCreateSubmit = useCallback(async (payload) => {
    setRegSubmitError(null);
    setRegCreateLoading(true);

    const staffShifts = plannedShifts.filter(
      (s) => s.staff_member_id === payload.staff_member_id && s.date === selectedDateStr
    );
    const segments = splitAtShiftBoundaries(payload.start_time, payload.end_time, staffShifts);

    let lastError = null;
    for (const seg of segments) {
      const result = await createManual({ ...payload, ...seg, pause_in_minutes: 0 });
      if (!result.success) { lastError = result.error; break; }
    }

    setRegCreateLoading(false);
    if (lastError) {
      setRegSubmitError(lastError ?? t('tidsregistering:attendance.create_failed'));
    } else {
      showSuccess(t('tidsregistering:attendance.created'));
      handleRegDialogClose();
      await loadDayData(selectedDateStr);
    }
  }, [plannedShifts, selectedDateStr, createManual, showSuccess, handleRegDialogClose, loadDayData, t]);

  // ── Planned shift drag (top track) ────────────────────────────────────────
  const handleShiftDragCreate = useCallback(({ category, startTime, endTime }) => {
    setShiftDialogInitial({ staffMember: category.staffMember, startTime, endTime });
    setShiftSubmitError(null);
    setShiftDialogOpen(true);
  }, []);

  const handleShiftDialogClose = useCallback(() => {
    setShiftDialogOpen(false);
    setShiftDialogInitial(null);
    setShiftSubmitError(null);
  }, []);

  const handleShiftCreateSubmit = useCallback(async (payload) => {
    setShiftSubmitError(null);
    setShiftCreateLoading(true);
    const result = await createShift(payload);
    setShiftCreateLoading(false);
    if (result.success) {
      showSuccess('Vagtplan oprettet');
      handleShiftDialogClose();
      await loadDayData(selectedDateStr);
    } else {
      setShiftSubmitError(result.error ?? 'Kunne ikke oprette vagtplan');
    }
  }, [createShift, showSuccess, handleShiftDialogClose, loadDayData, selectedDateStr]);

  // ── Edit existing registration (click on mødt block) ─────────────────────
  const handleSlotClick = useCallback((slot) => {
    const registrationId = slot.isLive
      ? slot.originalRegistration?.registration_id
      : slot.originalDetail?.registration_id;
    const actualRegistration = registrationId
      ? (registrations ?? []).find(r => r.registration_id === registrationId) ?? null
      : null;
    setEditDialogSlot({ ...slot, actualRegistration });
    setEditSubmitError(null);
    setEditDialogOpen(true);
  }, [registrations]);

  const handleEditDialogClose = useCallback(() => {
    setEditDialogOpen(false);
    setEditDialogSlot(null);
    setEditSubmitError(null);
  }, []);

  const resolveRegistrationId = (slot) => {
    if (slot?.isLive) return slot.originalRegistration?.registration_id ?? null;
    return slot?.originalDetail?.registration_id ?? null;
  };

  const handleEditSubmit = useCallback(async (payload) => {
    const registrationId = resolveRegistrationId(editDialogSlot);
    if (!registrationId) {
      setEditSubmitError('Kunne ikke finde registrerings-ID');
      return;
    }
    setEditSubmitError(null);
    setEditLoading(true);
    const result = await updateRegistration(registrationId, payload);
    setEditLoading(false);
    if (result.success) {
      showSuccess('Registrering opdateret');
      handleEditDialogClose();
      await loadDayData(selectedDateStr);
    } else {
      setEditSubmitError(result.error ?? 'Kunne ikke opdatere registrering');
    }
  }, [editDialogSlot, updateRegistration, showSuccess, handleEditDialogClose, loadDayData, selectedDateStr]);

  const handleEditDelete = useCallback(async () => {
    const registrationId = resolveRegistrationId(editDialogSlot);
    if (!registrationId) {
      setEditSubmitError('Kunne ikke finde registrerings-ID');
      return;
    }
    setEditDeleteLoading(true);
    const result = await deleteRegistration(registrationId);
    setEditDeleteLoading(false);
    if (result.success) {
      showSuccess('Registrering slettet');
      handleEditDialogClose();
      await loadDayData(selectedDateStr);
    } else {
      setEditSubmitError(result.error ?? 'Kunne ikke slette registrering');
    }
  }, [editDialogSlot, deleteRegistration, showSuccess, handleEditDialogClose, loadDayData, selectedDateStr]);

  const staffOverenskomstMap = useMemo(() => {
    const overenskomstById = new Map(overenskomster.map((o) => [o.overenskomst_id, o]));
    const map = {};
    for (const a of activeAssignments) {
      const overenskomst = overenskomstById.get(a.overenskomst_id);
      map[a.staff_member_id] = {
        overenskomst_id: a.overenskomst_id,
        overenskomst_name: overenskomst?.name ?? a.overenskomst_id,
      };
    }
    return map;
  }, [activeAssignments, overenskomster]);

  const categories = useMemo(() => {
    const flat = buildAttendanceCategories(
      plannedShifts.filter((s) => s.date === selectedDateStr),
      dayOverviews,
      selectedDateStr,
      registrationCodes,
      staff,
      registrations,
      staffOverenskomstMap,
    );
    // Grouped by agreement only when the user may see who is on which
    return canReadOverenskomster ? groupCategoriesByOverenskomst(flat) : flat;
  }, [plannedShifts, dayOverviews, selectedDateStr, registrationCodes, staff, registrations, staffOverenskomstMap, canReadOverenskomster]);

  const loading = overviewsLoading || shiftsLoading || codesLoading || staffLoading;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader
        mb={4}
        icon={AccessTimeIcon}
        title={t('tidsregistering:attendance.title')}
        subtitle={t('tidsregistering:page_subtitles.attendance')}
        actions={
          <DatePicker
            value={selectedDate}
            onChange={(date) => date && setSelectedDate(date)}
            slotProps={{ textField: { size: 'small' } }}
          />
        }
      />

      {overviewsError && (
        <Alert severity="error">{overviewsError}</Alert>
      )}

      {loading && categories.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <AttendanceChart
          categories={categories}
          selectedDate={selectedDateStr}
          onDragCreate={canCreateRegistration ? handleRegistrationDragCreate : null}
          onShiftDragCreate={canCreateShift ? handleShiftDragCreate : null}
          onSlotClick={canEditRegistration ? handleSlotClick : null}
          balancesMap={balancesMap}
          onFetchBalances={canReadBalances ? fetchBalancesForStaff : null}
          height="calc(100vh - 180px)"
          unavailabilityBlocks={unavailabilityBlocks}
        />
      )}

      {/* Registration creation dialog (bottom track drag) */}
      <CreateTimeEntryDialog
        open={regDialogOpen}
        onClose={handleRegDialogClose}
        onSubmit={handleRegCreateSubmit}
        staffMember={regDialogInitial?.staffMember ?? null}
        selectedDate={selectedDateStr}
        initialStartTime={regDialogInitial?.startTime ?? null}
        initialEndTime={regDialogInitial?.endTime ?? null}
        registrationCodes={registrationCodes}
        loading={regCreateLoading}
        submitError={regSubmitError}
      />

      {/* Edit existing registration dialog (click on mødt block) */}
      <EditTimeEntryDialog
        open={editDialogOpen}
        onClose={handleEditDialogClose}
        onSubmit={handleEditSubmit}
        onDelete={canDeleteRegistration ? handleEditDelete : undefined}
        slot={editDialogSlot}
        selectedDate={selectedDateStr}
        registrationCodes={registrationCodes}
        loading={editLoading}
        deleteLoading={editDeleteLoading}
        submitError={editSubmitError}
      />

      {/* Planned shift creation dialog (top track drag) */}
      <CreatePlannedShiftDialog
        open={shiftDialogOpen}
        onClose={handleShiftDialogClose}
        onSubmit={handleShiftCreateSubmit}
        staffMember={shiftDialogInitial?.staffMember ?? null}
        selectedDate={selectedDateStr}
        initialStartTime={shiftDialogInitial?.startTime ?? null}
        initialEndTime={shiftDialogInitial?.endTime ?? null}
        loading={shiftCreateLoading}
        submitError={shiftSubmitError}
      />
    </Box>
  );
}
